// The server renders the authoritative head (title, description, canonical,
// social tags, JSON-LD) for the URL that was requested. This module records
// that head before React mounts so client code can leave it alone on that
// URL and restore it when the visitor navigates back.

export interface HeadMeta {
  attr: 'name' | 'property';
  key: string;
  content: string;
}

export interface ServerHeadSnapshot {
  path: string;
  title: string;
  canonical: string;
  metas: HeadMeta[];
  jsonLd: string[];
}

const MANAGED_META = /^(description|robots|og:.+|twitter:.+|article:.+|geo\..+|ICBM)$/;

let snapshot: ServerHeadSnapshot | null | undefined;

export function normalisePath(pathname: string): string {
  const path = String(pathname || '/').split('?')[0].split('#')[0] || '/';
  return path.length > 1 ? path.replace(/\/+$/, '') || '/' : path;
}

export function isManagedMeta(key: string): boolean {
  return MANAGED_META.test(key);
}

export function managedMetas(): HTMLMetaElement[] {
  return Array.from(document.head.querySelectorAll('meta')).filter((meta) => {
    const key = meta.getAttribute('property') || meta.getAttribute('name') || '';
    return isManagedMeta(key);
  }) as HTMLMetaElement[];
}

export function captureServerHead(): ServerHeadSnapshot | null {
  if (snapshot !== undefined) return snapshot;
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    snapshot = null;
    return snapshot;
  }
  const root = document.getElementById('root');
  const serverRendered = Boolean(root && root.children.length > 0);
  if (!serverRendered) {
    snapshot = null;
    return snapshot;
  }
  const canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  snapshot = {
    path: normalisePath(window.location.pathname),
    title: document.title,
    canonical: canonical ? canonical.getAttribute('href') || '' : '',
    metas: managedMetas().map((meta) => ({
      attr: meta.hasAttribute('property') ? 'property' : 'name',
      key: meta.getAttribute('property') || meta.getAttribute('name') || '',
      content: meta.getAttribute('content') || '',
    })),
    jsonLd: Array.from(document.querySelectorAll('script[type="application/ld+json"]'))
      .map((script) => script.textContent || ''),
  };
  return snapshot;
}

export function serverHeadFor(pathname: string): ServerHeadSnapshot | null {
  const current = captureServerHead();
  if (!current) return null;
  return current.path === normalisePath(pathname) ? current : null;
}

export function resetServerHeadForTests(): void {
  snapshot = undefined;
}
